/* Archived turns. Past days are never deleted — the board is keyed by business
 * date, so yesterday's grid is still queryable for reporting. */
import * as Vue from 'vue';
import { store } from '../store.js';
import { ownClientsOnly } from '../rules/own-clients.js';
import { floorRead } from '../rules/missed-floor.js';

function shiftDate(key, days) {
  var p = key.split('-');
  var d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
  d.setDate(d.getDate() + days);
  return store.todayKey(d);
}

export const HistoryView = {
  name: 'HistoryView',
  setup: function () {
    var today = store.todayKey();
    var from = Vue.ref(shiftDate(today, -13));
    var to = Vue.ref(today);

    function run() { store.loadHistory(from.value, to.value); }
    Vue.onMounted(function () { if (!store.history.loaded) run(); });

    // date -> { parties, people } for walk-ins nobody could take
    var missedByDate = Vue.computed(function () {
      var out = {};
      (store.history.missed || []).forEach(function (m) {
        var d = out[m.businessDate] || (out[m.businessDate] = { parties: 0, people: 0 });
        d.parties += 1;
        d.people += m.partySize || 0;
      });
      return out;
    });

    /* ---------- a missed walk-in, read against the floor it happened on ----------
     *
     * "Six turned away" is the number that says something is wrong. It is not
     * the number that says what to do about it — that needs the two questions
     * the desk actually argues about afterwards: how many people were on, and
     * how many of them could have taken what was being asked for.
     *
     * Both are read off the turns rather than off the roster records. A day's
     * `present_since` is stamped when the day is opened, which on a day marked
     * in en masse is one timestamp for everybody and says nothing about who was
     * on the floor at ten to two. A technician's turns do: the first one they
     * started and the last one they finished is a shift, and a turn spanning
     * the moment is somebody who was mid-service in it.
     *
     * What a technician *can* do is read off today's roster, since that is the
     * only place it is recorded. A service list edited since will read as it is
     * now, not as it was — worth knowing before drawing a conclusion from a
     * fortnight ago.
     */
    function overlaps(t, from, to) { return t >= from && t <= to; }

    var missedDetail = Vue.computed(function () {
      var byDate = {};

      // Each technician's shift and their in-service stretches, per day.
      var shifts = {};
      store.history.rows.forEach(function (t) {
        var day = shifts[t.businessDate] || (shifts[t.businessDate] = {});
        var rec = day[t.staffId] || (day[t.staffId] = {
          staffId: t.staffId, name: t.staffName, from: t.startedAt, to: t.startedAt, spans: []
        });
        var end = t.endedAt == null ? t.startedAt : t.endedAt;
        if (t.startedAt < rec.from) rec.from = t.startedAt;
        if (end > rec.to) rec.to = end;
        rec.spans.push([t.startedAt, end]);
      });

      // How many the day had marked in, whether or not they got a turn, and
      // the desk's word on who was on their own clients that day.
      var marked = {}, dayRow = {};
      (store.history.dayStaff || []).forEach(function (d) {
        (dayRow[d.businessDate] || (dayRow[d.businessDate] = {}))[d.staffId] = d;
        if (!d.present) return;
        marked[d.businessDate] = (marked[d.businessDate] || 0) + 1;
      });

      (store.history.missed || []).forEach(function (m) {
        var at = m.createdAt;
        var day = shifts[m.businessDate] || {};
        var wanted = m.serviceIds || [];

        /* Everybody on the floor at that moment, then the rotation's question
           asked of them — rules/missed-floor.js. Somebody on their own clients
           counts as working but not as able: a walk-in does not go to them, and
           the stamped count beside it never included them. */
        var floor = [];
        Object.keys(day).forEach(function (id) {
          var rec = day[id];
          if (!overlaps(at, rec.from, rec.to)) return;
          var staff = store.staffOf(id);
          floor.push({
            id: id,
            name: rec.name,
            serviceIds: staff ? staff.serviceIds : [],
            ownClients: ownClientsOnly(staff, (dayRow[m.businessDate] || {})[id]),
            busy: rec.spans.some(function (s) { return overlaps(at, s[0], s[1]); })
          });
        });
        var read = floorRead(wanted, floor);
        var stampedBy = m.staffAbleByService || null;
        var nameOf = function (id) {
          var svc = store.serviceById.value[id];
          return svc ? svc.name : 'Removed service';
        };

        (byDate[m.businessDate] || (byDate[m.businessDate] = [])).push({
          id: m.id,
          at: at,
          partySize: m.partySize || 0,
          services: wanted.map(nameOf),
          anyService: !wanted.length,
          /* Who was in, off the row itself where the row has it.
             `staff_present` was stamped at the moment of the loss and is the
             better answer of the two: the day-wide count below is every name
             marked in at any point, so a day where two techs left at four
             o'clock reads the same at half past as it did at noon. The fallback
             is what rows written before the column existed still have. */
          marked: m.staffPresent == null ? (marked[m.businessDate] || 0) : m.staffPresent,
          markedStamped: m.staffPresent != null,
          /* Of those in, how many did that work — counted at the time, against
             the service lists as they read that day. The column beside it is
             the same question asked of today's roster and narrowed to whoever
             was mid-shift, so the two are a pair rather than a duplicate: this
             is the denominator, and a service list edited since cannot move it. */
          ableIn: m.staffAble == null ? null : m.staffAble,
          /* The wait the board was quoting for what they asked for. Only ever
             read off the row — it cannot be reconstructed, since the squares it
             was measured from have all stopped. Null is "nobody wrote it down"
             or "the board could not say", and both print as a dash. */
          wait: m.waitMinutes,
          working: read.working,
          able: read.able,
          free: read.free,
          freeNames: read.freeNames,
          /* One line per service they named, so a party after an acrylic and
             a gel pedicure reads as how many could have done each. Only worth
             a line each when there are two or more; one service is the union. */
          byService: wanted.length < 2 ? [] : read.byService.map(function (b) {
            return {
              serviceId: b.serviceId,
              name: nameOf(b.serviceId),
              able: b.able,
              ableIn: stampedBy && stampedBy[b.serviceId] != null ? stampedBy[b.serviceId] : null,
              free: b.free,
              freeNames: b.freeNames
            };
          })
        });
      });

      Object.keys(byDate).forEach(function (d) {
        byDate[d].sort(function (a, b) { return a.at - b.at; });
      });
      return byDate;
    });

    function ableTitle(row) {
      // The second number was counted at the time and does not drift; the first
      // is read off today's roster, so the tooltip says which is which rather
      // than letting "3 of 4" read as one measurement.
      var stamped = row.ableIn == null ? '' :
        ' ' + row.ableIn + ' in the rotation that day did that work, ' +
        'counted at the time.';
      if (row.anyService) {
        return 'No service was recorded, so everybody working in the rotation ' +
          'counts. Anybody on their own clients does not.' + stamped;
      }
      return 'Of the ' + row.working + ' working, this many in the rotation have ' +
        'at least one of those services on their list today. Anybody on their ' +
        'own clients does not count.' + stamped;
    }

    /* A dash rather than a nought for either way there is no number — a row
       written before the estimate was recorded, and a board that could not say.
       A nought is a real and rather damning answer here, so it gets its own
       word instead of a bare 0 that could be read as the missing case. */
    function waitText(row) {
      if (row.wait == null) return '—';
      return row.wait < 1 ? 'none' : store.formatMins(row.wait * 60000);
    }

    function waitTitle(row) {
      if (row.wait == null) {
        return 'No estimate recorded — either nobody in performed it and the ' +
          'board had no answer, or the row is older than the column.';
      }
      if (row.wait < 1) {
        return 'Somebody had room for it there and then. This one was there for ' +
          'the taking.';
      }
      return 'What Next availability was quoting for that work at that moment, ' +
        'if the day ran to time.';
    }

    function markedTitle(row) {
      return row.markedStamped
        ? row.marked + ' were marked in at that moment.'
        : row.marked + ' were marked in at some point that day — this row is ' +
          'older than the count being stamped as it happened, so it is the ' +
          'whole day rather than that minute.';
    }

    function serviceAbleTitle(row, b) {
      return 'Of the ' + row.working + ' working, how many in the rotation have ' +
        b.name + ' on their list today.' + (b.ableIn == null ? '' :
        ' ' + b.ableIn + ' in the rotation that day did it, counted at the time.');
    }

    function serviceFreeTitle(b) {
      return b.free ? b.freeNames.join(', ') + (b.free === 1 ? ' was' : ' were') +
        ' free for ' + b.name + ' at that moment.'
        : 'Everybody who does ' + b.name + ' was mid-service.';
    }

    function freeTitle(row) {
      if (!row.free) {
        return 'Everybody who could take it was mid-service. This one needed ' +
          'another technician, not a faster desk.';
      }
      return row.freeNames.join(', ') + (row.free === 1 ? ' was' : ' were') +
        ' free at that moment.';
    }

    // date -> { staff: [{name, turns, credit, requested, letters}], totals }
    var days = Vue.computed(function () {
      var byDate = {};
      store.history.rows.forEach(function (t) {
        var d = byDate[t.businessDate] || (byDate[t.businessDate] = {});
        var name = t.staffName || 'Removed tech';
        var rec = d[name] || (d[name] = {
          name: name, turns: 0, credit: 0, requested: 0, letters: [], totalMs: 0
        });
        rec.turns += 1;
        rec.credit += t.turnWeight;
        if (t.requested) rec.requested += 1;
        /* Every service the square recorded, not only the one its columns
           carry. A merged square is one turn and one row's worth of credit —
           the desk said so — but it is two services the technician performed,
           and the mix column is the one place on this table that is asked what
           was done rather than what it counted for. */
        rec.letters.push(t.serviceLetter);
        (t.extras || []).forEach(function (m) { rec.letters.push(m.serviceLetter || '?'); });
        if (t.endedAt) rec.totalMs += t.endedAt - t.startedAt;
      });
      return Object.keys(byDate).sort().reverse().map(function (date) {
        var staff = Object.keys(byDate[date])
          .map(function (n) { return byDate[date][n]; })
          .sort(function (a, b) { return b.credit - a.credit || a.name.localeCompare(b.name); });
        return {
          date: date,
          staff: staff,
          totalTurns: staff.reduce(function (s, r) { return s + r.turns; }, 0),
          totalCredit: staff.reduce(function (s, r) { return s + r.credit; }, 0),
          totalRequests: staff.reduce(function (s, r) { return s + r.requested; }, 0)
        };
      });
    });

    function label(date) {
      var p = date.split('-');
      var d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
      return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
    }

    function avg(rec) {
      if (!rec.turns || !rec.totalMs) return '—';
      return store.formatDuration(rec.totalMs / rec.turns);
    }

    return {
      from: from, to: to, run: run, days: days, label: label, avg: avg,
      missedByDate: missedByDate,
      missedDetail: missedDetail,
      ableTitle: ableTitle,
      serviceAbleTitle: serviceAbleTitle,
      serviceFreeTitle: serviceFreeTitle,
      freeTitle: freeTitle,
      waitText: waitText,
      waitTitle: waitTitle,
      markedTitle: markedTitle,
      formatClock: store.formatClock,
      history: store.history,
      formatCredit: store.formatCredit,
      ui: store.ui,
      pluralTurns: store.pluralTurns
    };
  },
  template: [
    '<div class="config">',
    '  <div class="config__intro">',
    '    <h2>History</h2>',
    '    <p><span>Every past day is kept. Turn totals below count service weight, so a half-turn service adds 0.5 and a zero-turn service adds nothing. Walk-ins nobody could take are logged beside each day.</span></p>',
    '  </div>',
    '',
    '  <div class="add-card">',
    '    <div class="add-card__row">',
    '      <label class="field"><span class="field__label">From</span>',
    '        <input v-model="from" type="date" class="input" /></label>',
    '      <label class="field"><span class="field__label">To</span>',
    '        <input v-model="to" type="date" class="input" /></label>',
    '      <button type="button" class="btn btn--primary" @click="run">Load</button>',
    '    </div>',
    '  </div>',
    '',
    '  <p v-if="history.loading" class="config__empty">Loading…</p>',
    '  <p v-else-if="history.error" class="warn-line">{{ history.error }}</p>',
    '  <p v-else-if="!days.length" class="config__empty">No turns recorded in this range.</p>',
    '',
    '  <div v-for="day in days" :key="day.date" class="day-card">',
    '    <div class="day-card__head">',
    '      <h3>{{ label(day.date) }}</h3>',
    '      <span class="day-card__sum">',
    '        <span>{{ day.totalTurns }} {{ day.totalTurns === 1 ? "service" : "services" }} &middot; {{ pluralTurns(day.totalCredit) }}</span>',
    '        <span v-if="ui.requestsOn && day.totalRequests"> &middot; {{ day.totalRequests }} {{ day.totalRequests === 1 ? "request" : "requests" }}</span>',
    '      </span>',
    '    </div>',
    '    <p v-if="missedByDate[day.date]" class="day-card__missed">',
    '      {{ missedByDate[day.date].people }} missed',
    '      {{ missedByDate[day.date].people === 1 ? "walk-in" : "walk-ins" }}',
    '      <span>across {{ missedByDate[day.date].parties }}',
    '        {{ missedByDate[day.date].parties === 1 ? "party" : "parties" }}</span></p>',
    /* Each one against the floor it happened on: how many were working, how
       many of them do that kind of work, and how many of those were free. The
       last column is the one that says whether it was a staffing problem. */
    '    <div v-if="missedDetail[day.date]" class="table-scroll">',
    '      <table class="report report--missed">',
    '        <thead>',
    '          <tr><th>Turned away</th><th>People</th><th>Wanted</th>',
    '            <th title="The wait the board was quoting for that work at that moment, recorded as it happened">Wait then</th>',
    '            <th title="Technicians with a turn either side of that moment — the board&rsquo;s own record of who was on the floor">Working</th>',
    '            <th title="Of those working and in the rotation, how many have one of those services on their list — and, after it, how many in the rotation did that work, counted at the time. A party after two or more services gets a line for each">Could take it</th>',
    '            <th title="Of those who could take it, how many were not mid-service">Free</th></tr>',
    '        </thead>',
    '        <tbody>',
    '          <tr v-for="row in missedDetail[day.date]" :key="row.id">',
    '            <td class="report__name">{{ formatClock(row.at) }}</td>',
    '            <td>{{ row.partySize }}</td>',
    '            <td class="report__wanted">',
    '              <span v-if="row.anyService" class="report__muted">not given</span>',
    '              <template v-else>{{ row.services.join(", ") }}</template></td>',
    '            <td :title="waitTitle(row)" :class="row.wait == null ? \'report__none\' : \'\'">',
    '              {{ waitText(row) }}</td>',
    '            <td :title="row.marked ? markedTitle(row) : \'\'">',
    '              {{ row.working }}<span v-if="row.marked" class="report__muted"> of {{ row.marked }}</span></td>',
    '            <td :title="ableTitle(row)">{{ row.able }}',
    '              <span v-if="row.ableIn != null" class="report__muted"> of {{ row.ableIn }}</span>',
    /* Each service on its own line under the total: how many could have done
       the acrylic, and how many the gel pedicure. */
    '              <span v-for="b in row.byService" :key="b.serviceId" class="report__by-svc"',
    '                :title="serviceAbleTitle(row, b)">{{ b.name }}: {{ b.able }}',
    '                <span v-if="b.ableIn != null" class="report__muted"> of {{ b.ableIn }}</span></span></td>',
    '            <td :title="freeTitle(row)" :class="row.free ? \'report__free\' : \'report__none\'">',
    '              {{ row.free }}',
    '              <span v-for="b in row.byService" :key="b.serviceId" class="report__by-svc"',
    '                :class="b.free ? \'report__free\' : \'report__none\'"',
    '                :title="serviceFreeTitle(b)">{{ b.name }}: {{ b.free }}</span></td>',
    '          </tr>',
    '        </tbody>',
    '      </table>',
    '    </div>',
    '    <div class="table-scroll">',
    '      <table class="report">',
    '        <thead>',
    '          <tr><th>Technician</th><th>Services</th><th>Turns</th>',
    '            <th v-if="ui.requestsOn">Requests</th>',
    '            <th>Avg time</th><th>Mix</th></tr>',
    '        </thead>',
    '        <tbody>',
    '          <tr v-for="rec in day.staff" :key="rec.name">',
    '            <td class="report__name">{{ rec.name }}</td>',
    '            <td>{{ rec.turns }}</td>',
    '            <td>{{ formatCredit(rec.credit) }}</td>',
    '            <td v-if="ui.requestsOn">{{ rec.requested || "—" }}</td>',
    '            <td>{{ avg(rec) }}</td>',
    '            <td class="report__mix">{{ rec.letters.join(" ") }}</td>',
    '          </tr>',
    '        </tbody>',
    '      </table>',
    '    </div>',
    '  </div>',
    '</div>'
  ].join('\n')
};
